import { Injectable, inject } from '@angular/core';
import { Map } from 'maplibre-gl';
import { WindElement } from 'src/app/models/wind';
import { SettingsService, MapSkin } from 'src/app/services/account/settings.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

interface WindParticle {
  lng: number;
  lat: number;
  age: number;
  maxAge: number;
  prevX: number;
  prevY: number;
}

/** The part of the map the wind canvas covers, in world pixels at the zoom it was created at. */
interface WindCanvasArea {
  west: number;
  north: number;
  worldSize: number;
  // Canvas pixels per world pixel (below 1 only when the area would make the canvas too large)
  scale: number;
}

const WIND_SOURCE = 'wind-canvas';
const WIND_LAYER = 'wind-canvas-layer';

/**
 * Animated wind particles over the map. The animation plays once per calculation, while the camera shows the whole
 * range (or route), then fades away and is removed: nothing stays on the map afterwards. It is timed to end within
 * the startup range overview (NavigationMotionControllerService.RANGE_OVERVIEW_HOLD_MS, 5 s), so the wind is gone
 * before the camera zooms in.
 *
 * The particles are drawn into a MapLibre canvas source pinned to the map, so the wind is a map layer of its own: just
 * above the base map's land, water and roads, below its labels and below everything the app draws (range, routes,
 * charging stations, cursor). Being pinned to the ground, the trails also stay in place when the map moves.
 */
@Injectable({
  providedIn: 'root'
})
export class WindService {

  private mapInstance: Map | null = null;
  private windCanvas: HTMLCanvasElement | null = null;
  private windCtx: CanvasRenderingContext2D | null = null;
  private canvasArea: WindCanvasArea | null = null;
  private windData: WindElement[] = [];
  private windAnimationId: number | null = null;
  private windParticles: WindParticle[] = [];
  private windBounds = { minLng: 0, maxLng: 0, minLat: 0, maxLat: 0 };
  private windLookupMap: { [key: string]: WindElement } = {};
  private windCellSize = 0.5;
  private readonly PARTICLE_COUNT_MIN = 400;
  private readonly PARTICLE_COUNT_MAX = 1000;
  // The original 90-frame animation sped up to fit the overview: ~4 s of play instead of 6 s at 15 fps
  private readonly TARGET_FPS = 22;
  private readonly FADE_ALPHA = 0.06;
  // Play and fade-out end this long after initWind(), whatever the frame rate: a beat before the 5 s overview ends
  private readonly TOTAL_MS = 4800;
  private readonly FADE_OUT_MS = 800;
  // Line width in screen pixels at the zoom the animation started at
  private readonly LINE_WIDTH = 2;
  // Largest canvas side; a pitched view reaching far towards the horizon is drawn at a lower resolution instead
  private readonly MAX_CANVAS_PX = 2048;
  // The wind blue of the light and dark map skins
  private readonly LIGHT_SKIN_COLOR = 'rgba(32,86,140,0.8)';
  private readonly DARK_SKIN_COLOR = 'rgba(96,172,236,0.8)';
  private lastFrameTime = 0;
  private deadline = 0;
  private fadingOut = false;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeOutTimer: ReturnType<typeof setTimeout> | null = null;
  private onSettledHandler: (() => void) | null = null;

  private readonly settingsService = inject(SettingsService);
  private readonly utilsService = inject(UtilsService);

  /** Play the wind animation for a new calculation; animate = false (background refreshes) plays nothing. */
  public initWind(mapInstance: Map, windElements: WindElement[], animate = true): void {
    this.clear();
    if (!mapInstance || windElements.length === 0 || !animate || this.prefersReducedMotion()) return;
    this.mapInstance = mapInstance;
    this.windData = windElements;
    this.buildWindLookup();
    this.initWindParticles();
    this.deadline = performance.now() + this.TOTAL_MS;
    // The caller frames the range right after handing over the wind: start once that camera move has finished, so
    // the animation plays while the whole range is on screen
    this.startTimer = setTimeout(() => {
      this.startTimer = null;
      this.startWhenSettled();
    }, 0);
  }

  public clear(): void {
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.fadeOutTimer !== null) {
      clearTimeout(this.fadeOutTimer);
      this.fadeOutTimer = null;
    }
    if (this.windAnimationId !== null) {
      cancelAnimationFrame(this.windAnimationId);
      this.windAnimationId = null;
    }
    if (this.mapInstance && this.onSettledHandler) {
      this.mapInstance.off('moveend', this.onSettledHandler);
    }
    this.onSettledHandler = null;
    this.removeWindLayer();
    this.windCanvas = null;
    this.windCtx = null;
    this.canvasArea = null;
    this.windData = [];
    this.windParticles = [];
    this.windLookupMap = {};
    this.fadingOut = false;
  }

  private prefersReducedMotion(): boolean {
    return typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private startWhenSettled(): void {
    const map = this.mapInstance;
    if (!map) return;
    if (map.isMoving()) {
      this.onSettledHandler = () => {
        this.onSettledHandler = null;
        this.startWhenSettled();
      };
      map.once('moveend', this.onSettledHandler);
      return;
    }
    // The camera took so long that there is no time left to play before the deadline
    if (performance.now() >= this.deadline - this.FADE_OUT_MS || !this.addWindLayer()) {
      this.clear();
      return;
    }
    this.startWindAnimation();
  }

  /** A canvas source covering the visible part of the wind data, as a raster layer just above the base map. */
  private addWindLayer(): boolean {
    const map = this.mapInstance;
    if (!map) return false;
    const view = map.getBounds();
    const west = Math.max(view.getWest(), this.windBounds.minLng - this.windCellSize);
    const east = Math.min(view.getEast(), this.windBounds.maxLng + this.windCellSize);
    const south = Math.max(view.getSouth(), this.windBounds.minLat - this.windCellSize, -85);
    const north = Math.min(view.getNorth(), this.windBounds.maxLat + this.windCellSize, 85);
    if (west >= east || south >= north) return false;

    // One canvas pixel per world pixel at the current zoom, so a flat map shows the canvas at its own resolution
    const worldSize = 512 * Math.pow(2, map.getZoom());
    const width = this.worldX(east, worldSize) - this.worldX(west, worldSize);
    const height = this.worldY(south, worldSize) - this.worldY(north, worldSize);
    const scale = Math.min(1, this.MAX_CANVAS_PX / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil(width * scale));
    canvas.height = Math.max(1, Math.ceil(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    try {
      map.addSource(WIND_SOURCE, {
        type: 'canvas',
        canvas,
        animate: true,
        coordinates: [[west, north], [east, north], [east, south], [west, south]]
      });
      map.addLayer({
        id: WIND_LAYER,
        type: 'raster',
        source: WIND_SOURCE,
        paint: {
          'raster-opacity': 1,
          'raster-opacity-transition': { duration: this.FADE_OUT_MS, delay: 0 },
          'raster-fade-duration': 0,
          'raster-resampling': 'linear'
        }
      }, this.findWindBeforeId());
    } catch {
      // addSource/addLayer throw while a new style is still loading
      this.removeWindLayer();
      return false;
    }
    this.windCanvas = canvas;
    this.windCtx = ctx;
    this.canvasArea = { west: this.worldX(west, worldSize), north: this.worldY(north, worldSize), worldSize, scale };
    return true;
  }

  /**
   * Directly below the first label or GeoJSON-backed layer: above the base map's own land, water and roads, below
   * its labels and everything the app adds, all of which use GeoJSON sources (route lines are inserted right under
   * the labels, so the wind must sit below those too).
   */
  private findWindBeforeId(): string | undefined {
    const style = this.mapInstance?.getStyle();
    if (!style) return undefined;
    return style.layers.find(layer => {
      if (layer.type === 'symbol' && 'text-field' in (layer.layout || {})) return true;
      const sourceId = 'source' in layer ? layer.source : undefined;
      return typeof sourceId === 'string' && style.sources[sourceId]?.type === 'geojson';
    })?.id;
  }

  private removeWindLayer(): void {
    const map = this.mapInstance;
    if (!map) return;
    try {
      if (map.getLayer(WIND_LAYER)) map.removeLayer(WIND_LAYER);
      if (map.getSource(WIND_SOURCE)) map.removeSource(WIND_SOURCE);
    } catch {
      // The map was already removed (component teardown); its layers went with it
    }
  }

  private worldX(lng: number, worldSize: number): number {
    return (lng + 180) / 360 * worldSize;
  }

  private worldY(lat: number, worldSize: number): number {
    const sin = Math.sin(lat * Math.PI / 180);
    return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * worldSize;
  }

  private buildWindLookup(): void {
    this.windLookupMap = {};
    if (this.windData.length < 2) return;

    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;
    for (const w of this.windData) {
      if (w.Location.lng < minLng) minLng = w.Location.lng;
      if (w.Location.lng > maxLng) maxLng = w.Location.lng;
      if (w.Location.lat < minLat) minLat = w.Location.lat;
      if (w.Location.lat > maxLat) maxLat = w.Location.lat;
    }
    this.windBounds = { minLng, maxLng, minLat, maxLat };

    let colSize = 0;
    const firstLng = this.windData[0].Location.lng;
    for (let i = 1; i < this.windData.length; i++) {
      if (this.windData[i].Location.lng !== firstLng) {
        colSize = i;
        break;
      }
    }
    if (colSize > 1) {
      this.windCellSize = Math.abs(this.windData[1].Location.lat - this.windData[0].Location.lat) || 0.5;
    }

    for (const w of this.windData) {
      const key = `${Math.round(w.Location.lng / this.windCellSize)},${Math.round(w.Location.lat / this.windCellSize)}`;
      this.windLookupMap[key] = w;
    }
  }

  private getWindAt(lng: number, lat: number): WindElement | null {
    const key = `${Math.round(lng / this.windCellSize)},${Math.round(lat / this.windCellSize)}`;
    return this.windLookupMap[key] || null;
  }

  private initWindParticles(): void {
    const count = Math.min(Math.max(this.windData.length, this.PARTICLE_COUNT_MIN), this.PARTICLE_COUNT_MAX);
    this.windParticles = [];
    for (let i = 0; i < count; i++) {
      this.windParticles.push(this.createWindParticle(true));
    }
  }

  private createWindParticle(randomAge: boolean): WindParticle {
    const b = this.windBounds;
    return {
      lng: b.minLng + Math.random() * (b.maxLng - b.minLng),
      lat: b.minLat + Math.random() * (b.maxLat - b.minLat),
      age: randomAge ? Math.floor(Math.random() * 120) : 0,
      maxAge: 80 + Math.floor(Math.random() * 60),
      prevX: -1,
      prevY: -1
    };
  }

  private startWindAnimation(): void {
    const frameInterval = 1000 / this.TARGET_FPS;
    const animate = (timestamp: number) => {
      this.windAnimationId = requestAnimationFrame(animate);
      const elapsed = timestamp - this.lastFrameTime;
      if (elapsed < frameInterval) return;
      this.lastFrameTime = timestamp - (elapsed % frameInterval);
      this.drawWindFrame();
    };
    this.windAnimationId = requestAnimationFrame(animate);
  }

  private drawWindFrame(): void {
    const area = this.canvasArea;
    if (!this.windCanvas || !this.windCtx || !area || !this.mapInstance || this.windData.length === 0) return;

    const ctx = this.windCtx;
    const w = this.windCanvas.width;
    const h = this.windCanvas.height;

    // Fade existing trails — this creates the tail effect
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0,0,0,${this.FADE_ALPHA})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';

    const skin = this.settingsService.getMap().Skin;
    const isLight = skin === MapSkin.Light;
    const zoom = this.mapInstance.getZoom();
    const speedFactor = 0.0004 * Math.pow(2, 7 - zoom);

    // Single batched stroke for all particles
    ctx.strokeStyle = isLight ? this.LIGHT_SKIN_COLOR : this.DARK_SKIN_COLOR;
    ctx.lineWidth = this.LINE_WIDTH * area.scale;
    ctx.beginPath();

    for (let i = 0; i < this.windParticles.length; i++) {
      const p = this.windParticles[i];
      p.age++;

      if (p.age > p.maxAge) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      const wind = this.getWindAt(p.lng, p.lat);
      if (!wind || wind.Speed === 0 || wind.Opacity === 0) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      const dir = this.utilsService.toRad(wind.Angle);
      const speed = wind.Speed * speedFactor;
      p.lng -= Math.sin(dir) * speed;
      p.lat -= Math.cos(dir) * speed;

      // Canvas pixels: the canvas is pinned to the ground, so this is independent of the camera
      const x = (this.worldX(p.lng, area.worldSize) - area.west) * area.scale;
      const y = (this.worldY(p.lat, area.worldSize) - area.north) * area.scale;

      if (x < -10 || x > w + 10 || y < -10 || y > h + 10) {
        this.windParticles[i] = this.createWindParticle(false);
        continue;
      }

      if (p.prevX >= 0) {
        ctx.moveTo(p.prevX, p.prevY);
        ctx.lineTo(x, y);
      }
      p.prevX = x;
      p.prevY = y;
    }

    ctx.stroke();

    if (!this.fadingOut && performance.now() >= this.deadline - this.FADE_OUT_MS) {
      this.fadeOut();
    }
  }

  /** The particles keep moving while the layer fades away; then the animation is removed. */
  private fadeOut(): void {
    const map = this.mapInstance;
    this.fadingOut = true;
    if (map?.getLayer(WIND_LAYER)) map.setPaintProperty(WIND_LAYER, 'raster-opacity', 0);
    this.fadeOutTimer = setTimeout(() => {
      this.fadeOutTimer = null;
      this.clear();
    }, this.FADE_OUT_MS);
  }
}
